import { useCallback } from 'react';
import { Address, useAddress, useCreateAddress } from 'src/graphql/entities/address';
import { ConditionInput, SortingInput } from 'src/graphql/types';
import { useNotifications } from 'src/utils/hooks';
import { AppEvents, emitAppEvent } from 'src/utils/hooks/use-app-events';
import { AddSiteFormFields, NewAddressFormValues } from './use-new-site-form';

export interface SiteListCacheOptions {
  filters?: ConditionInput[];
  limit?: number;
  sort?: SortingInput;
  offset?: number;
}

/** Creates or updates the site, then links the chosen clients to a new one. */
export function useSaveSite({
  address,
  cacheOptions,
}: {
  address?: Address | null;
  cacheOptions?: SiteListCacheOptions;
}) {
  const { showErrorAlert } = useNotifications();
  const { createAddress } = useCreateAddress(cacheOptions);
  const { updateAddress } = useAddress(address?.addressId);
  const { associateClientsWithAddress } = useAddress();

  return useCallback(
    async (values: NewAddressFormValues, partnerId: number): Promise<Address | undefined> => {
      const commonValues = {
        [AddSiteFormFields.Address1]: values[AddSiteFormFields.Address1],
        [AddSiteFormFields.Address2]: values[AddSiteFormFields.Address2],
        [AddSiteFormFields.BusinessName]: values[AddSiteFormFields.BusinessName],
        [AddSiteFormFields.City]: values[AddSiteFormFields.City],
        [AddSiteFormFields.State]: values[AddSiteFormFields.State]?.value,
        [AddSiteFormFields.OrgNotes]: values[AddSiteFormFields.OrgNotes],
        [AddSiteFormFields.Zip]: values[AddSiteFormFields.Zip],
        ownerId: values[AddSiteFormFields.AccountManager]?.id,
        brandId: values[AddSiteFormFields.ClientSegment]?.id,
      };

      if (address) {
        const [updated] =
          (await updateAddress({
            ...address,
            ...commonValues,
            platformUser: values[AddSiteFormFields.AccountManager],
          })) ?? [];
        return updated ? { ...address, ...updated } : undefined;
      }

      // `useCreateAddress` surfaces the user-facing error message (including duplicate-address errors).
      const created = await createAddress({ ...commonValues, partnerId });
      const clients = values[AddSiteFormFields.Clients];
      if (!created || !clients.length) {
        return created;
      }

      const clientIds = clients.map((client) => client.id);
      const associated = await associateClientsWithAddress({
        clientIds,
        addressId: created.addressId,
        partnerId,
      });
      const withClients = { ...created, primaryClientId: clientIds[0] };
      if (!associated) {
        showErrorAlert('Failed to associate clients with address');
      }
      emitAppEvent(AppEvents.ClientsToAddressAssociation, {
        address: withClients,
        clientIds,
      });
      return withClients;
    },
    [address, associateClientsWithAddress, createAddress, showErrorAlert, updateAddress],
  );
}
